import { type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import {
  ArrowRight,
  Boxes,
  CircleArrowDown,
  FileCode2,
  FileText,
  Gem,
  Gift,
  Image as ImageIcon,
  Package,
  Sparkles,
  Store,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import RewardsProgramTermsModal from '@/components/RewardsProgramTermsModal';
import { selectV2 } from '../v2Upload.slice';
import { plural } from '../utils/fileHelpers';
import { V2File } from '../v2Upload.d';
import {
  CoinStack,
  Confetti,
  CornerSwoosh,
  LadderBadge,
  SuccessMark,
} from './submittedArt';

/** A count of one kind of file: the mark in its own tint, the number, the name. */
function Stat({
  icon,
  tone,
  value,
  label,
}: {
  icon: ReactNode;
  tone: string;
  value: number;
  label: string;
}) {
  return (
    <div className="flex flex-1 items-center gap-3 px-5 py-1">
      <span
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
          tone
        )}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block font-primary text-2xl font-semibold leading-none">
          {value}
        </span>
        <span className="mt-1 block truncate text-[12px] text-muted-foreground">
          {label}
        </span>
      </span>
    </div>
  );
}

type RungKind = 'submitted' | 'approved' | 'rated' | 'rewards';

/** One rung of the ladder: its badge, and what earns it. */
function Rung({
  n,
  kind,
  label,
}: {
  n: number;
  kind: RungKind;
  label: string;
}) {
  return (
    <div className="flex w-[86px] flex-col items-center gap-2 text-center">
      <LadderBadge kind={kind} />
      <span className="text-[11px] leading-tight text-muted-foreground">
        {n}. {label}
      </span>
    </div>
  );
}

const LADDER: Array<{ kind: RungKind; label: string }> = [
  { kind: 'submitted', label: 'Project submitted' },
  { kind: 'approved', label: 'Project approved' },
  { kind: 'rated', label: 'Get 4+ Star Ratings' },
  { kind: 'rewards', label: 'Unlock rewards' },
];

const REWARDS: Array<{ icon: ReactNode; tone: string; label: string }> = [
  {
    icon: <Sparkles className="h-4 w-4" />,
    tone: 'bg-success/15 text-success',
    label: 'Free perks & credits',
  },
  {
    icon: <Gift className="h-4 w-4" />,
    tone: 'bg-violet-100 text-violet-600',
    label: 'Gift cards & cash rewards',
  },
  {
    icon: <Store className="h-4 w-4" />,
    tone: 'bg-warning/15 text-warning',
    label: 'Store discounts & more',
  },
  {
    icon: <Gem className="h-4 w-4" />,
    tone: 'bg-info/15 text-info',
    label: 'Exclusive member benefits',
  },
];

const countKind = (files: V2File[], kind: V2File['kind']) =>
  files.filter((f) => f.kind === kind).length;

/** Design files against the paperwork: the CAD section counts them apart, so
 * the summary of what was sent does too. */
const PAPERWORK_EXTS = ['pdf', 'txt', 'md', 'rtf', 'doc', 'docx'];

/**
 * What happens after Submit for approval: what was sent, and what the project
 * can earn once it is published.
 *
 * Every mark on this screen is drawn (see submittedArt). It used to open on a
 * 1.9MB PNG carrying the same milestones as flat artwork — which could not show
 * the project's own numbers, and was the largest file the wizard shipped.
 */
// fallow-ignore-next-line complexity
export default function SubmittedScreen({
  title,
  onUploadAnother,
  onViewPost,
  group,
}: {
  title: string;
  onUploadAnother: () => void;
  onViewPost?: () => void;
  group?: { workpieces: number; files: number };
}) {
  const { files, videoLinks } = useSelector(selectV2);
  const images = countKind(files, 'image');
  const gcode = countKind(files, 'gcode');
  const docs = files.filter((f) => f.kind === 'doc');
  const paperwork = docs.filter((f) => PAPERWORK_EXTS.includes(f.ext)).length;
  const design = docs.length - paperwork;
  const total = files.length + videoLinks.length;

  return (
    <div className="mx-auto w-full max-w-[1340px] space-y-5 p-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-success/[0.07] via-card to-success/[0.04] p-8">
        <Confetti />
        <CornerSwoosh />

        <div className="relative flex flex-col items-center gap-7 text-center md:flex-row md:text-left">
          <SuccessMark />
          <div className="flex-1">
            <h2 className="font-primary text-3xl font-semibold tracking-tight">
              Project upload complete!
            </h2>
            <p className="mt-2 text-[15px] text-muted-foreground">
              {group ? (
                <>
                  Nice work — &ldquo;{title}&rdquo; and its {group.workpieces}{' '}
                  workpieces are with our reviewers, and publish as soon as they
                  are happy with them.
                </>
              ) : (
                <>
                  Nice work — &ldquo;{title}&rdquo; is with our reviewers, and
                  publishes as soon as they are happy with it.
                </>
              )}
            </p>
          </div>
          <div className="rounded-2xl border bg-card px-8 py-5 text-center shadow-sm">
            <div className="font-primary text-4xl font-semibold text-success">
              100%
            </div>
            <div className="mt-0.5 text-[13px] text-muted-foreground">
              Complete
            </div>
          </div>
        </div>

        {/* What was sent */}
        <div className="relative mt-7 rounded-2xl border bg-card shadow-sm">
          <div
            className={cn(
              'flex flex-wrap items-center gap-y-4 divide-x py-4',
              group && 'mx-auto max-w-[520px]'
            )}
          >
            {group ? (
              <>
                <Stat
                  icon={<Boxes className="h-5 w-5" />}
                  tone="bg-success/15 text-success"
                  value={group.workpieces}
                  label={`${plural(
                    group.workpieces,
                    'Workpiece',
                    'Workpieces'
                  )} submitted`}
                />
                <Stat
                  icon={<Upload className="h-5 w-5" />}
                  tone="bg-info/15 text-info"
                  value={group.files}
                  label={`${plural(group.files, 'File', 'Files')} uploaded`}
                />
              </>
            ) : (
              <>
                <Stat
                  icon={<Upload className="h-5 w-5" />}
                  tone="bg-success/15 text-success"
                  value={total}
                  label={`${plural(total, 'File', 'Files')} uploaded`}
                />
                <Stat
                  icon={<ImageIcon className="h-5 w-5" />}
                  tone="bg-info/15 text-info"
                  value={images}
                  label={plural(images, 'Image', 'Images')}
                />
                <Stat
                  icon={<FileCode2 className="h-5 w-5" />}
                  tone="bg-violet-100 text-violet-600"
                  value={gcode}
                  label={`G-code ${plural(gcode, 'file', 'files')}`}
                />
                <Stat
                  icon={<Package className="h-5 w-5" />}
                  tone="bg-orange-100 text-orange-600"
                  value={design}
                  label={`Design ${plural(design, 'file', 'files')}`}
                />
                <Stat
                  icon={<FileText className="h-5 w-5" />}
                  tone="bg-warning/15 text-warning"
                  value={paperwork}
                  label={`Documentation ${plural(paperwork, 'file', 'files')}`}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rewards */}
      <div className="rounded-2xl border bg-warning/[0.06] p-7">
        <div className="flex flex-wrap items-center justify-center gap-7 xl:flex-nowrap">
          <div className="shrink-0">
            <CoinStack />
          </div>

          <div className="w-[230px] shrink-0">
            <h3 className="font-primary text-xl font-semibold">
              Earn Filefinity Coins
            </h3>
            <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
              Upload quality projects and get rewarded. The better your projects
              perform, the more you earn!
            </p>
            <RewardsProgramTermsModal
              trigger={
                <button
                  type="button"
                  className="mt-3 flex items-center gap-1.5 text-[13px] font-medium text-success hover:underline"
                >
                  <CircleArrowDown className="h-4 w-4" />
                  Learn how rewards work
                </button>
              }
            />
          </div>

          {/* One row with arrows: these four are a sequence, which is what the
              numbering is for. */}
          <div className="flex shrink-0 items-start justify-center gap-1">
            {LADDER.map((rung, i) => (
              <div key={rung.kind} className="flex items-start">
                <Rung n={i + 1} kind={rung.kind} label={rung.label} />
                {i < LADDER.length - 1 && (
                  <ArrowRight className="mt-4 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>

          <div className="min-w-[300px] flex-1 rounded-2xl border bg-card p-5">
            <div>
              <div className="mb-3 text-[13px] font-semibold">
                Unlock amazing rewards
              </div>
              <div className="space-y-2">
                {REWARDS.map((reward) => (
                  <span
                    key={reward.label}
                    className="flex items-center gap-2 text-[12px]"
                  >
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg',
                        reward.tone
                      )}
                    >
                      {reward.icon}
                    </span>
                    {reward.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 pb-2">
        <Button variant="outline" onClick={onUploadAnother}>
          Upload another
        </Button>
        {onViewPost && (
          <Button onClick={onViewPost}>
            {group ? 'View the project' : 'View uploaded post'}
          </Button>
        )}
      </div>
    </div>
  );
}
